import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { isPublicPage } from "@/utils/visualBuilder";

import { useUrlParams } from "./useUrlParams";
import { getCurrentProperty } from "../store/actions";
import { currentPropertySelector } from "../store/selectors";
import { isRequestAllowed } from "../utils/helper";

// Global variable to track if a property fetch is in progress across all hook instances
const isFetching = { current: false };

/**
 * Custom hook to fetch and manage current property data.
 * Ensures only one fetch happens at a time using a global `isFetching` flag.
 * @param {Object} [options] - Options for the hook
 * @param {boolean} [options.skipInitialFetch=false] - Whether to skip the initial fetch
 */
export const useCurrentPropertyData = ({ skipInitialFetch = false } = {}) => {
  const dispatch = useDispatch();
  const { status: propertyReqStatus } = useSelector(currentPropertySelector);
  const { propertyId, userId, tabStatus } = useUrlParams();

  // Fetches the current property if no fetch is in progress
  const fetchCurrentProperty = () => {
    if (isFetching.current) return;

    isFetching.current = true;
    dispatch(getCurrentProperty(propertyId, userId, isPublicPage(tabStatus))).finally(() => {
      isFetching.current = false;
    });
  };

  // Fetches the property on mount or when dependencies change, if conditions are met
  useEffect(() => {
    if (propertyId && !skipInitialFetch && isRequestAllowed(propertyReqStatus)) {
      fetchCurrentProperty();
    }
  }, [propertyId, userId, tabStatus, skipInitialFetch, dispatch]);
};
